<template>
  <FullScreenPopup
    ref="popup"
    :title="title"
    :lock-scroll="true"
    :closeable="closeable"
    :min-width="minWidth"
    :max-width="maxWidth"
    @visibility-change="handleVisibilityChange"
  >
    <div class="confirmation-popup">
      <div class="confirmation-popup__content">
        <component :is="getIcon" class="confirmation-popup__icon" />
        <slot>
          <p class="confirmation-popup__message">{{ message }}</p>
        </slot>
      </div>
      <div class="confirmation-popup__controls-wrapper">
        <Divider type="horizontal" />
        <div class="confirmation-popup__controls">
          <component
            v-for="(control, index) in controls"
            :key="index"
            :is="getButtonComponent(control.type)"
            size="small"
            class="confirmation-popup__control-button"
            :is-loading="control.loading || false"
            @click="handleControlClick(control)"
          >
            {{ control.label }}
          </component>
        </div>
      </div>
    </div>
  </FullScreenPopup>
</template>

<script>
import FullScreenPopup from '../full-screen-popup';
import { Divider } from '@/shared/ui/divider';
import { BlueButton, WhiteButton } from '@/shared/ui/form';
import { PopupTitle } from '@/shared/ui/full-screen-popup';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@/shared/ui/icons';

export default {
  props: {
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value),
    },
    icon: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    controls: {
      type: Array,
      default: () => [],
      validator: (value) => value.every(
        (control) => control.label &&
          ['regular', 'default'].includes(control.type) &&
          typeof control.event === 'string'
      ),
    },
    defaultEvent: {
      type: String,
      default: null,
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    minWidth: {
      type: Number,
      default: 150,
    },
    maxWidth: {
      type: Number,
      default: 600,
    },
  },
  data() {
    return {
      data: null,
      eventFired: false,
      inlineHandlers: {},
    };
  },
  components: { FullScreenPopup, PopupTitle, BlueButton, WhiteButton, Divider },
  computed: {
    getIcon() {
      if (this.icon) return this.icon;
      return { success: SuccessIcon, error: ErrorIcon, warning: WarningIcon, info: InfoIcon }[this.type];
    },
  },
  methods: {
    open(data = null) {
      this.data = data;
      this.eventFired = false;
      this.inlineHandlers = {};
      this.$refs.popup.open();

      const builder = {
        on: (event, handler) => {
          this.inlineHandlers[event] = handler;
          return builder;
        },
      };

      return builder;
    },
    close() {
      this.$refs.popup.close();
    },
    togglePopup() {
      this.$refs.popup.togglePopup();
    },
    getButtonComponent(type) {
      return type === 'default' ? BlueButton : WhiteButton;
    },
    handleControlClick(control) {
      const inlineHandler = this.inlineHandlers[control.event];
      if (inlineHandler) {
        inlineHandler({ payload: control?.payload, data: this.data });
      } else {
        this.$emit(control.event, { payload: control?.payload, data: this.data });
      }
      this.eventFired = true;
      this.$refs.popup.close();
    },
    handleVisibilityChange({ id, isVisible }) {
      if (id !== this.$refs.popup.getPopupId) return;

      this.$emit('visibility-change', { id, isVisible, data: this.data });

      if (!isVisible && !this.eventFired && this.defaultEvent) {
        const inlineHandler = this.inlineHandlers[this.defaultEvent];
        if (inlineHandler) {
          inlineHandler({ data: this.data });
        } else {
          this.$emit(this.defaultEvent, { data: this.data });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.confirmation-popup {
  &__icon {
    float: left;
    width: 50px;
    height: 50px;
    background-repeat: no-repeat;
    background-position: center center;
    margin: 0 10px 0 0;
  }

  &__message {
    text-align: justify;
  }

  &__controls {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
  }
}
</style>
